import { AxiosHeaders, type AxiosResponse } from "@octagon/axios";
import { fileDownloadApiInstance } from "./apiInstance";

/**
 * Downloads a file by its correlation ID and filename.
 *
 * @param {string} corrId - The correlation ID of the file to download.
 * @return {void}
 */
export function downloadFileByCorrId(corrId?: string | null): Promise<void> {
  if (!corrId) {
    console.error('Invalid correlation ID');
    return Promise.reject(new Error('Error with file download: Invalid correlation ID'));
  }

  const params = {
    correlationId: corrId
  };

  // Make a request to download the file. Thrown errors are caught by the calling code
  return makeFileDownloadRequest('/download-file', params).then(handleDownloadResponse)
    .catch((error) => {
      console.error('Error in downloadFileByCorrId:', error);
      return Promise.reject(new Error('Error with file download request'));
    });
}

/**
 * Downloads a template file.
 *
 * @return {void}
 */
export function downloadTemplate(): Promise<void> {
  // Make a request to download the template.
  return makeFileDownloadRequest('/download-template').then(handleDownloadResponse).catch((error) => {
    console.error('Error in downloadTemplate:', error);
    return Promise.reject(new Error('Error downloading template'));
  });
}

/**
 * Makes a request to download a file.
 *
 * @param {string} path - The path of the endpoint.
 * @param {Record<string, string>} params - The parameters for the request.
 * @return {Promise<AxiosResponse<any, any>>} A promise that resolves to the response.
 */
function makeFileDownloadRequest(path: string, params?: Record<string, string>): Promise<AxiosResponse<any, any>> {
  return fileDownloadApiInstance.get(path, {
    responseType: 'blob', // Important to handle binary data
    params,
  });
}

/**
 * Handles the response of a file download.
 *
 * @param {AxiosResponse<any, any>} response - The response of the file download request.
 * @return {void}
 */
function handleDownloadResponse(response: AxiosResponse<any, any>): void {
  // Get the Content-Disposition header without relying on a specific header class instance.
  const headers = response?.headers as AxiosHeaders | Record<string, string> | undefined;
  let disposition = '';

  if (headers && typeof (headers as AxiosHeaders).get === 'function') {
    disposition = ((headers as AxiosHeaders).get('Content-Disposition')
      || (headers as AxiosHeaders).get('content-disposition')
      || '') as string;
  } else if (headers) {
    disposition = (headers['Content-Disposition'] || headers['content-disposition'] || '') as string;
  }

  // Extract and sanitize the filename from the Content-Disposition header.
  const fileNameMatch = disposition.match(/filename\s*=\s*"?([^";]+)"?/i);
  const rawName = fileNameMatch?.[1]?.trim() || 'download.xlsx';
  const fileName = rawName.replace(/[/\\]/g, ''); // strip path separators

  // Create a URL for the blob.
  const url = window.URL.createObjectURL(new Blob([response.data]));

  // Adding a link to download the file to the DOM
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);

  // Click the link to download the file.
  link.click();

  // Remove the link from the DOM
  link.parentNode?.removeChild(link);

  // Revoke the object URL to free up memory.
  window.URL.revokeObjectURL(url);
}

